/* import __COLOCATED_TEMPLATE__ from './pane.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { useResource } from 'ember-resources';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import { ChildAction } from 'embercom/objects/inbox/command-k/action';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ConvertToTicketAction from 'embercom/objects/inbox/command-k/convert-to-ticket';
import { TicketTypeResource } from 'embercom/components/inbox2/ticket-type-resource';
import type CommandKService from 'embercom/services/command-k';
import { inject as service } from '@ember/service';
import { type CommandKPaneComponentArgs } from 'embercom/lib/inbox2/command-k-types';
import { TicketCategory } from 'embercom/objects/inbox/ticket';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import { EmojiIcon, NamedIcon } from 'embercom/objects/inbox/command-k/icons';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type Router from '@ember/routing/router-service';

interface Signature {
  Args: CommandKPaneComponentArgs<ConvertToTicketAction>;
}

export default class ConvertToTicketPaneComponent extends Component<Signature> {
  @tracked query = '';
  @service declare commandK: CommandKService;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare intercomEventService: any;

  selection = useResource(this, NavigableSelection, () => ({
    items: this.items,
    isCommandKVisible: this.commandK.isVisible,
  }));

  ticketTypeResource = useResource(this, TicketTypeResource, () => ({
    allowedTicketCategories: [TicketCategory.Request],
  }));

  get emptyTicketTypes() {
    return this.items.length === 0;
  }

  get emptyTicketTypeInfo() {
    return {
      icon: 'ticket' as const,
      header: this.intl.t(`inbox.command-k.tickets.empty-state.header`, {
        ticket_type: this.intl.t(`inbox.command-k.tickets.singular.request`),
      }),
      linkText: this.intl.t(`inbox.command-k.tickets.empty-state.link-text`, {
        ticket_type: this.intl.t(`inbox.command-k.tickets.singular.request`),
      }),
      subText: this.intl.t(`inbox.command-k.tickets.empty-state.request`),
    };
  }

  get items(): Array<ChildAction> {
    let upperQuery = this.query.trim().toLocaleUpperCase();

    return this.ticketTypeResource.activeShareableTicketTypes
      .filter((ticketType) => ticketType.name.toLocaleUpperCase().includes(upperQuery))
      .sortBy('name')
      .map((ticketType) => {
        let { id, name } = ticketType;

        return new ChildAction({
          parent: this.args.actionItem,
          id: id.toString(),
          label: name,
          value: ticketType.id,
          icon: new EmojiIcon(ticketType.emoji || undefined),
        });
      });
  }

  get itemsList() {
    let togetherList = [...this.items];
    togetherList.push(
      new ChildAction({
        parent: this.args.actionItem,
        id: '5000',
        label: this.intl.t('inbox.command-k.actions.manage-ticket-types'),
        value: {
          id: 'ticket-types-settings',
          category: undefined,
          categoryIcon: new NamedIcon('settings'),
          newTicket: false,
        },
        icon: new NamedIcon('settings'),
      }),
    );

    return togetherList;
  }

  get app() {
    return this.session.workspace;
  }

  @action search(query: string) {
    this.query = query;

    this.commandK.trackSearch({
      query: this.query,
      number_of_results: this.items.length,
    });
  }

  @action settingsClicked() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'ticket_intervention_inbox_conversation_convert_to_customer_ticket_popup_settings',
      place: 'inbox',
      context: 'inbox.conversation.convert_to_customer_ticket_popup',
      section: 'convert_to_customer_ticket_popup',
    });
    this.mangeTicketTypes();
  }

  @action mangeTicketTypes() {
    let url: string = this.router.urlFor(
      'apps.app.settings.helpdesk.tickets',
      this.session.workspace,
      {
        queryParams: { tab: 'ticket-types' },
      },
    );
    safeWindowOpen(url);
  }

  get inputPlaceholder() {
    return this.intl.t(`inbox.command-k.actions.convert-to-customer-ticket`);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::ConvertToTicket::Pane': typeof ConvertToTicketPaneComponent;
    'inbox2/command-k/convert-to-ticket/pane': typeof ConvertToTicketPaneComponent;
  }
}
